import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CurecallCheckbox, CurecallInput, CurecallToggle } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { formatCombinedIsoDuration, parseCombinedIsoDuration } from 'src/utils/duration-iso.util'

type TGracePeriodParts = { days: number; hours: number; minutes: number }

export default function SendingRules() {
  const { t } = useTranslation()
  const { setValue, getValues, watch } = useFormContext()

  const preventRescheduleOnceTriggered: boolean = watch('schedule.preventRescheduleOnceTriggered') ?? true

  const [gracePeriod, setGracePeriod] = useState<TGracePeriodParts>(() => {
    const iso = getValues('schedule.gracePeriodDuration')
    if (!iso) return { days: 0, hours: 0, minutes: 0 }
    const { days, hours, minutes } = parseCombinedIsoDuration(iso)
    return { days, hours, minutes }
  })

  const [isGracePeriodEnabled, setIsGracePeriodEnabled] = useState<boolean>(
    () => !!getValues('schedule.gracePeriodDuration'),
  )

  const updateGracePeriod = (next: TGracePeriodParts) => {
    setGracePeriod(next)
    const isZero = next.days === 0 && next.hours === 0 && next.minutes === 0
    setValue('schedule.gracePeriodDuration', isZero ? null : formatCombinedIsoDuration(next))
  }

  const handlePartChange =
    (part: keyof TGracePeriodParts) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Math.max(0, Number(event.target.value) || 0)
      updateGracePeriod({ ...gracePeriod, [part]: value })
    }

  const handleToggleGracePeriod = (isEnabled: boolean) => {
    setIsGracePeriodEnabled(isEnabled)
    if (!isEnabled) {
      setGracePeriod({ days: 0, hours: 0, minutes: 0 })
      setValue('schedule.gracePeriodDuration', null)
    }
  }

  return (
    <div className="c-flex c-flex-col c-border-t c-border-grey-400 c-py-2">
      <h3 className="c-my-2 c-p-0 c-font-nexa c-text-xs c-font-bold c-uppercase c-tracking-wider c-text-grey-600 c-pb-2">
        {t('pages.configuration.messages.sending-rules-section-title')}
      </h3>

      <div className="c-flex c-flex-col c-gap-5">
        <CurecallCheckbox
          isByDefaultChecked={preventRescheduleOnceTriggered}
          onChange={(e) => setValue('schedule.preventRescheduleOnceTriggered', e.target.checked)}
          label={t('pages.configuration.messages.prevent-reschedule-label')}
          tooltipContent={t('pages.configuration.messages.prevent-reschedule-tooltip')}
          tooltipPosition="right"
        />

        <div className="c-flex c-flex-col c-gap-4">
          <CurecallToggle
            isEnabled={isGracePeriodEnabled}
            onChange={handleToggleGracePeriod}
            leftLabel={t('pages.configuration.messages.grace-period-label')}
            tooltipContent={t('pages.configuration.messages.grace-period-tooltip')}
            tooltipPosition="right"
          />

          {isGracePeriodEnabled && (
            <div className="c-flex c-items-end c-gap-3">
              <span className="c-font-nexa c-text-sm c-text-grey-900 c-pb-2">
                {t('pages.configuration.messages.grace-period-prefix')}
              </span>
              <CurecallInput
                label={t('pages.configuration.messages.grace-period-days')}
                type="number"
                min="0"
                value={gracePeriod.days}
                onChange={handlePartChange('days')}
                wrapperClassName="c-w-20"
              />
              <CurecallInput
                label={t('pages.configuration.messages.grace-period-hours')}
                type="number"
                min="0"
                value={gracePeriod.hours}
                onChange={handlePartChange('hours')}
                wrapperClassName="c-w-20"
              />
              <CurecallInput
                label={t('pages.configuration.messages.grace-period-minutes')}
                type="number"
                min="0"
                value={gracePeriod.minutes}
                onChange={handlePartChange('minutes')}
                wrapperClassName="c-w-20"
              />
              <span className="c-font-nexa c-text-sm c-text-grey-900 c-pb-2">
                {t('pages.configuration.messages.grace-period-suffix')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
